import React, { useMemo, useReducer } from 'react'

import { useExportJobs } from '@containers/export-items/export-items-state'
import { type ErrorDetails } from '@containers/export-items/export-queries'
import { useApi } from '@core/api'
import ErrorHandler from '@core/api/ErrorHandler'
import { PreValidateExportInput } from '@core/api/generated'
import { addSnack } from '@core/snack/snack-state'

type Item = {
  contentType: string
  id: string
  identifier: string
  savedContent: {
    content: {
      question?: string
      passage?: string
      custom_passage?: string
      answers?: { status: string; value: string }[]
    }
  }[]
}

const extension = {
  csv: 'csv',
  txt: 'txt',
  qtiv2p1: 'xml',
  comptia: 'xls',
  surpass: 'xls',
}

type State = {
  projectIds: string[] | null
  items: Item[] | null
  itemIds: string[] | null
  open: boolean
  anchorEl: HTMLElement | null
  loading: boolean
  creatingJobFor: string
  projectName: string
  fileName?: string
}

type Action = (Partial<State> & { type?: undefined }) | { type: 'reset'; items?: Item[] }

const initialState: State = {
  projectIds: null,
  items: null,
  itemIds: null,
  anchorEl: null,
  loading: false,
  open: false,
  creatingJobFor: '',
  projectName: '',
  fileName: '',
}

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'reset':
      return { ...initialState, items: action.items || null }

    default:
      return { ...state, ...action }
  }
}

export const useDownloadItems = () => {
  const api = useApi()
  const [state, setState] = useReducer(reducer, initialState)

  const [queryKey, queryInput] = React.useMemo((): [string, PreValidateExportInput] => {
    const itemIds = state.itemIds || state.items?.map((item) => item.id) || []

    let key = ''

    if (!itemIds.length && !state.projectIds?.length) {
      return ['', { folderIds: [], itemIds: [] }]
    }

    if (state.projectIds?.length) {
      key = state.projectIds.join(',')
    }

    if (itemIds.length) {
      key += itemIds.join(',')
    }

    return [key, { folderIds: state.projectIds || [], itemIds }]
  }, [state.projectIds, state.itemIds, state.items])

  return useMemo(
    () => ({
      queryKey,
      queryInput,
      state,
      setState,

      onCancel() {
        setState({ type: 'reset' })
      },

      onClose() {
        setState({ open: false })
      },

      onOpenV2(
        anchorEl: HTMLElement,
        params: { itemIds?: string[]; folderIds?: string[]; downloadFileName?: string },
      ) {
        if (!params.folderIds?.length && !params.itemIds?.length) {
          console.warn('useDownloadItems: No itemIds or folderIds provided for download menu')
        }

        setState({
          itemIds: params.itemIds || null,
          projectIds: params.folderIds || null,
          loading: !params.itemIds,
          anchorEl,
          projectName: params.downloadFileName || 'Generate items',
          fileName: '',
          open: true,
        })
      },

      onOpen(
        projectId: string | string[],
        projectName: string,
        anchorEl: HTMLElement,
        items?: Item[],
      ) {
        setState({
          items,
          loading: !items,
          projectIds: Array.isArray(projectId) ? projectId : [projectId],
          projectName,
          fileName: '',
          anchorEl,
          open: true,
        })
      },

      async onExport(contentType: string, itemIds: string[], alias?: string) {
        if (!itemIds.length) {
          return addSnack({
            message: 'Error while downloading items. Empty List.',
            severity: 'warning',
          })
        }

        setState({ creatingJobFor: contentType })

        const fileName = state.fileName || undefined
        const label = fileName
          ? `${fileName.replaceAll(' ', '_')}.${
              extension[contentType.toLocaleLowerCase()]
            } (${alias || contentType})`
          : undefined
        const id = (label || contentType) + new Date()
        try {
          const response = await api.exportItems({
            body: {
              export_type: contentType,
              item_ids: itemIds,
              file_name: fileName,
            },
          })
          const jobId = response.data?.job_id

          if (jobId) {
            useExportJobs.getState().add({
              id: jobId,
              status: 'pending',
              createdAt: new Date().toISOString(),
              fileName: fileName || '',
              label: label || '',
              exportType: contentType,
              totalCompleted: 0,
              totalFailed: 0,
              totalItems: itemIds.length,
              errorDetails: {} as ErrorDetails,
            })
          }

          setState({ creatingJobFor: '', anchorEl: null })
        } catch (error) {
          const { message } = ErrorHandler(error) as any
          addSnack({
            message: `Error while downloading items. ${message}`,
            severity: 'error',
          })
          useExportJobs.getState().remove(id)
          setState({ creatingJobFor: '' })
          throw error
        }
      },
    }),
    [state],
  )
}

export type DownloadMenuState = ReturnType<typeof useDownloadItems>
